<template>
    <div
        class="modal-container modal-container-dialog"
        :class="{ hidden: !display, closing: closing }"
        tabindex="-1"
        role="dialog"
        :aria-hidden="!display"
        :style="styleObject"
        @keydown="keyDownHandle"
        @animationend="onAnimationEnd"
        @mousedown="stopPropagationEvent"
        @touchstart="stopPropagationEvent"
        @click="stopPropagationEvent"
    >
        <div class="modal-out-close-area" @mousedown="onMouseDown" @touchstart="onTouchOutside"></div>
        <slot @mousedown="stopPropagationEvent" @touchstart="stopPropagationEvent" @click="stopPropagationEvent"></slot>
    </div>
</template>

<script lang="ts">
import { useVModel } from "@/utils/v-model";
import { defineComponent } from "vue";

export default defineComponent({
    name: "ModalDialogContainer",
    emits: ["update:display", "key", "close"],
    props: {
        display: Boolean,
        lockClose: Boolean,
        static: Boolean,
        closeCallback: Function,
        zIndex: {
            type: Number,
            default: undefined,
        },
    },
    setup(props) {
        return {
            displayStatus: useVModel(props, "display"),
        };
    },
    data() {
        return {
            closing: false,
            returnFocusTarget: this.display ? (document.activeElement as HTMLElement | null) : null,
        };
    },
    methods: {
        restoreFocus: function () {
            const target = this.returnFocusTarget;
            this.returnFocusTarget = null;

            if (!target?.isConnected || target.matches(":disabled")) {
                return;
            }

            // A callback may already have moved focus to another view or a new dialog.
            if (document.activeElement === document.body || this.$el.contains(document.activeElement)) {
                target.focus({ preventScroll: true });
            }
        },

        close: function (forced?: boolean) {
            if (this.lockClose && forced !== true) {
                return;
            }
            if (this.closeCallback && forced !== true) {
                this.closeCallback(() => {
                    this.$emit("close");
                    this.closing = true;
                });
            } else {
                this.$emit("close");
                this.closing = true;
            }
        },

        onAnimationEnd: function (e: AnimationEvent) {
            e.stopPropagation();
            if (e.animationName === "modal-close-animation") {
                this.closing = false;
                this.displayStatus = false;
            }
        },

        keyDownHandle: function (e: KeyboardEvent) {
            e.stopPropagation();
            if (e.key === "Escape" && this.display && !this.closing) {
                this.close();
            } else {
                this.$emit("key", e);
            }
        },

        stopPropagationEvent: function (e) {
            e.stopPropagation();
        },

        onTouchOutside: function (e: TouchEvent) {
            e.stopPropagation();
            if (!this.static) {
                this.close();
            }
        },

        onMouseDown: function (e: MouseEvent) {
            e.stopPropagation();
            if (e.button === 0 && !this.static) {
                this.close();
            }
        },
    },
    computed: {
        styleObject(): Record<string, string> {
            return this.zIndex === undefined ? {} : { zIndex: `${this.zIndex}` };
        },
    },
    beforeUnmount: function () {
        this.restoreFocus();
    },
    watch: {
        display: function () {
            if (this.display) {
                this.closing = false;
                this.returnFocusTarget = document.activeElement as HTMLElement | null;
            } else {
                this.$nextTick(this.restoreFocus);
            }
        },
    },
});
</script>
